import type { Cue } from '@/lib/cues.js';

/**
 * The decision logic behind hands-free start, as a pure state machine.
 *
 * It lives apart from the hook because this is the part that can be wrong in
 * ways the user would experience as "it never starts" or "it started while I
 * was still sitting down" — and neither is observable from a screenshot. A
 * reducer over (state, now, conditionsMet) is testable to the millisecond.
 */
export interface AutoStartConfig {
  /** Conditions must hold this long before recording begins. */
  holdMs: number;
  /** Dropouts shorter than this do not reset the hold. */
  graceMs: number;
  /** Silence before the first "still not right" nag. */
  nagAfterMs: number;
  nagEveryMs: number;
  /**
   * Sounds for failure: the countdown being lost, and the slow nag. Off — in
   * real use they went off while the phone was still being placed and were the
   * sounds people asked to remove. The checklist on screen says what is wrong.
   */
  failureCues: boolean;
}

export const AUTO_START_DEFAULTS: AutoStartConfig = {
  holdMs: 3000,
  graceMs: 700,
  nagAfterMs: 8000,
  nagEveryMs: 12000,
  failureCues: false,
};

export interface AutoStartState {
  holdStartedAt: number | null;
  lastMetAt: number | null;
  failingSince: number | null;
  lastNagAt: number;
  readyCuePlayed: boolean;
  fired: boolean;
}

export function initialAutoStartState(): AutoStartState {
  return {
    holdStartedAt: null,
    lastMetAt: null,
    failingSince: null,
    lastNagAt: 0,
    readyCuePlayed: false,
    fired: false,
  };
}

export interface AutoStartStep {
  state: AutoStartState;
  /** Cues to play, in order. */
  cues: Cue[];
  /** 0–1 through the hold. */
  progress: number;
  /** Whole seconds remaining, or null when not counting down. */
  secondsLeft: number | null;
  /** True exactly once, on the tick the session should begin. */
  start: boolean;
}

export function autoStartStep(
  state: AutoStartState,
  input: { now: number; conditionsMet: boolean },
  config: AutoStartConfig = AUTO_START_DEFAULTS,
): AutoStartStep {
  const { now, conditionsMet } = input;
  const next: AutoStartState = { ...state };
  const cues: Cue[] = [];

  if (state.fired) {
    return { state: next, cues, progress: 0, secondsLeft: null, start: false };
  }

  if (conditionsMet) {
    next.lastMetAt = now;
    next.failingSince = null;
    next.holdStartedAt ??= now;
    if (!next.readyCuePlayed) {
      next.readyCuePlayed = true;
      cues.push('ready');
    }
  } else {
    const withinGrace = state.lastMetAt !== null && now - state.lastMetAt <= config.graceMs;
    if (!withinGrace) {
      if (state.holdStartedAt !== null && config.failureCues) {
        // A countdown was running and we lost it — say so once, immediately.
        cues.push('notReady');
        next.lastNagAt = now;
      }
      next.holdStartedAt = null;
      next.readyCuePlayed = false;
      next.failingSince ??= now;
    }
    // Inside the grace window the hold is left untouched and keeps advancing.
  }

  if (next.holdStartedAt !== null) {
    const elapsed = now - next.holdStartedAt;
    const progress = Math.min(1, elapsed / config.holdMs);
    // The grace window preserves progress across a brief dropout, but the
    // trigger itself still needs a good frame: otherwise a hold that completes
    // *during* a dropout starts recording at the one moment we cannot see the
    // user — which is the opposite of what the hold is for.
    if (progress >= 1 && conditionsMet) {
      next.fired = true;
      cues.push('start');
      return { state: next, cues, progress: 1, secondsLeft: 0, start: true };
    }
    if (progress >= 1) {
      // Ready to go, waiting for the shot to come back.
      return { state: next, cues, progress: 1, secondsLeft: 0, start: false };
    }
    return {
      state: next,
      cues,
      progress,
      secondsLeft: Math.max(0, Math.ceil((config.holdMs - elapsed) / 1000)),
      start: false,
    };
  }

  // Not counting down: nag slowly while the shot stays unusable.
  const failingFor = next.failingSince === null ? 0 : now - next.failingSince;
  if (config.failureCues && failingFor >= config.nagAfterMs && now - next.lastNagAt >= config.nagEveryMs) {
    next.lastNagAt = now;
    cues.push('notReady');
  }

  return { state: next, cues, progress: 0, secondsLeft: null, start: false };
}
